import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Warning, ArrowLeft } from '@phosphor-icons/react';
import { errorStatus } from '../lib/error';
import { useHotel, useHotelRooms } from '../hooks/useHotel';
import { usePropertyRating, usePropertyReviews } from '../hooks/useReviews';
import { HotelGallery } from '../components/hotel/HotelGallery';
import {
  HotelHeader, AmenityList, PolicyBlock, ReviewsSection, HotelDetailSkeleton,
} from '../components/hotel/HotelSections';
import { RoomList } from '../components/hotel/RoomList';
import { BookingWidget } from '../components/hotel/BookingWidget';

/**
 * Hotel detail (/hotel/:id). Two queries: detail (by id) + rooms (by id+dates+guests).
 * Stay context lives in the URL (carried from search / deep links). Reserve navigates
 * to /book/:id with the chosen room + stay. See docs/hotel-detail/.
 */
export default function HotelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);

  const checkIn = params.get('checkIn') ?? '';
  const checkOut = params.get('checkOut') ?? '';
  const guests = Number(params.get('guests')) || 1;

  const hotelQ = useHotel(id);
  const ratingQ = usePropertyRating(id);
  const reviewsQ = usePropertyReviews(id);
  const roomsQ = useHotelRooms(id, {
    checkIn: checkIn || undefined,
    checkOut: checkOut || undefined,
    guests,
  });

  const rooms = roomsQ.data?.rooms ?? [];
  const availableCount = roomsQ.data?.availableCount ?? 0;
  const hasDates = !!checkIn && !!checkOut && checkOut > checkIn;

  // Drop a selection that's no longer valid after a refetch.
  useEffect(() => {
    if (!selectedUnitId) return;
    const r = rooms.find((x) => x.unitId === selectedUnitId);
    if (!r || !(r.available && r.fits && r.meetsMinStay)) setSelectedUnitId(null);
  }, [rooms, selectedUnitId]);

  function changeStay(next: { checkIn: string; checkOut: string; guests: number }) {
    setParams(
      (prev) => {
        const m = new URLSearchParams(prev);
        next.checkIn ? m.set('checkIn', next.checkIn) : m.delete('checkIn');
        next.checkOut ? m.set('checkOut', next.checkOut) : m.delete('checkOut');
        m.set('guests', String(next.guests));
        return m;
      },
      { replace: true },
    );
  }

  function reserve() {
    if (!selectedUnitId) return;
    const q = new URLSearchParams({
      unitId: selectedUnitId,
      ...(checkIn && { checkIn }),
      ...(checkOut && { checkOut }),
      guests: String(guests),
    });
    navigate(`/book/${id}?${q.toString()}`);
  }

  function focusWidgetDates() {
    document.querySelector<HTMLElement>('[aria-label="Check-in date"]')?.focus();
  }

  // Detail-level loading / 404 / error gate the whole page.
  if (hotelQ.isPending) {
    return (
      <div className="min-h-screen bg-cream px-5 py-8 sm:px-8 lg:px-14 2xl:px-20">
        <HotelDetailSkeleton />
      </div>
    );
  }
  if (hotelQ.isError) {
    const notFound = errorStatus(hotelQ.error) === 404;
    return (
      <div className="min-h-screen bg-cream">
        <div className="mx-auto max-w-2xl px-5 py-24 text-center">
          <Warning className="mx-auto h-12 w-12 text-clay" />
          <h1 className="mt-4 font-serif text-2xl font-bold text-ink">
            {notFound ? "This hotel isn't available" : 'Something went wrong'}
          </h1>
          {notFound ? (
            <Link to="/search" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-clay px-6 py-3 text-sm font-semibold text-white transition hover:bg-clay-dark">
              <ArrowLeft className="h-4 w-4" /> Back to hotels
            </Link>
          ) : (
            <button type="button" onClick={() => hotelQ.refetch()} className="mt-6 rounded-full bg-clay px-6 py-3 text-sm font-semibold text-white transition hover:bg-clay-dark">
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const hotel = hotelQ.data;

  return (
    <div className="min-h-screen bg-cream px-5 py-8 sm:px-8 lg:px-14 2xl:px-20">
      <Link
        to="/search"
        className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted transition hover:text-clay"
      >
        <ArrowLeft className="h-4 w-4" /> All hotels
      </Link>

      <HotelGallery images={hotel.images} gallery={hotel.gallery} name={hotel.name} />

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_380px]">
        {/* Content */}
        <div className="space-y-8">
          <HotelHeader hotel={hotel} summary={ratingQ.data} />
          {hotel.tagLine && <p className="text-lg leading-relaxed text-ink-soft">{hotel.tagLine}</p>}
          {hotel.description && (
            <section className="border-t border-sand pt-8">
              <h2 className="font-serif text-2xl font-bold text-ink">About this hotel</h2>
              <p className="mt-3 whitespace-pre-line leading-relaxed text-ink-soft">{hotel.description}</p>
            </section>
          )}

          {roomsQ.isError ? (
            <section className="border-t border-sand pt-8">
              <div className="rounded-2xl border border-sand bg-white p-8 text-center">
                <p className="font-semibold text-ink">
                  {errorStatus(roomsQ.error) === 429 ? 'Too many requests — try again shortly.' : "Couldn't load rooms."}
                </p>
                <button type="button" onClick={() => roomsQ.refetch()} className="mt-4 rounded-full bg-clay px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-clay-dark">
                  Retry
                </button>
              </div>
            </section>
          ) : roomsQ.isPending ? (
            <section className="border-t border-sand pt-8">
              <div className="space-y-3">
                {[0, 1].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-cream-deep" />)}
              </div>
            </section>
          ) : (
            <RoomList
              rooms={rooms}
              guests={guests}
              hasDates={hasDates}
              availableCount={availableCount}
              selectedUnitId={selectedUnitId}
              onSelect={setSelectedUnitId}
              onChangeDates={focusWidgetDates}
            />
          )}

          <AmenityList amenities={hotel.amenities} />
          <PolicyBlock hotel={hotel} />
          <ReviewsSection
            summary={ratingQ.data}
            reviews={reviewsQ.data}
            isLoading={ratingQ.isPending || reviewsQ.isPending}
          />
        </div>

        {/* Sticky booking widget */}
        <aside>
          <div className="lg:sticky lg:top-6">
            <BookingWidget
              currency={hotel.currency}
              checkIn={checkIn}
              checkOut={checkOut}
              guests={guests}
              rooms={rooms}
              selectedUnitId={selectedUnitId}
              onChangeStay={changeStay}
              onReserve={reserve}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
